import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Container, Draggable } from 'react-smooth-dnd'
import { orderBy } from 'lodash'
import Select from 'react-select'
import axios from 'axios'
import { IconButton, Button } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { convertDataToMarkdownFormat } from '@/utils/appUtils'
import { HelperService } from '@/services/helper.service'
import {
  deleteQuestionFromDocument,
  generateCustomQuestionAnswer,
  generateQuestionAnswerBasedonNumberOfInput,
  loadMoreQuestionsAndAnswersBasedOnDocument,
  updateQnasSortOrder,
  updateQuestionFromDocument,
} from '@/store/knowledge/actions'
import AutoTextArea from '@/components/UI/AutoResizeTextArea/AutoResizeTextArea'
import { BrainlyMathEditorSimple } from '@/components/UI/TextAreaNewVersion'
import RichMarkdownContent from '@/components/UI/RichMarkdownContent'

interface Props {
  agent_id?: string
  author_id?: string
  created_at?: string
  file_id?: string
  file_name?: string
  keywords?: string[]
  name?: string
  organisation_id?: string
  qas?: []
  updated_at?: string
  type?: string
  _id?: string
  index?: number
  section?: any
  documentLength?: number
  last_offset?: number
  retryExecution?: boolean
  qnasLoading?: boolean
  questionsLoading?: boolean
  qasFetchError?: boolean
  isQuizView?: boolean
  isSummarised?: boolean
  isSummarisedWithTypeSummary?: boolean
  setSelectedQuestions?: (questions: any) => void
  updatedAnswersHandler?: (answers: any) => void
  setAnswerGenerationProcessing?: (value: boolean) => void
  addMoreQuestionHandler?: (e: any, _id: string) => void
  setAnswerData?: (answerData: any) => void
  toolkitGenerateQna?: boolean
  buildKnowledge?: boolean
  lastQuesRef?: any
}

const helper = new HelperService()

const QAnsAListSummarised = (props: Props) => {
  const { t } = useTranslation('translation')
  let setInterval: any = null
  const dispatch = useDispatch()
  const [lastOffset, setLastOffset] = useState(0)
  const [generateQnaForm, setGenerateQnaForm] = useState<any>({
    generate: props.toolkitGenerateQna ? 10 : null,
    generateLoading: false,
    apiCount: 0,
    error: false,
    retry: null,
    retryLoading: false,
    summaryLoading: false,
  })

  const [Qnas, setQnas] = useState<any[]>(props.qas)
  const [query, setQuery] = useState<any>({
    document_id: '',
    question: '',
    answer: '',
    sort_order: 0,
    _id: '',
  })
  const questionTypes = [
    {
      value: 'tutoring',
      label: t('tutoring'),
    },
    {
      value: 'socratic',
      label: t('socratic'),
    },
  ]
  const [questionType, setQuestionType] = useState(questionTypes[0])
  const [hasGenerateTriggred, setHasGenerateTriggred] = useState(false)

  let source = axios.CancelToken.source()

  useEffect(() => {
    if (props.toolkitGenerateQna || props.buildKnowledge) {
      generateQuestionsHandler('generate', 10)
      return () => {
        source.cancel('CANCEL FROM QnaListSummarised')
      }
    }
  }, [props.toolkitGenerateQna, props.buildKnowledge])

  useEffect(() => {
    if (props.qas && props.qas.length > 0 && !props.retryExecution) {
      setHasGenerateTriggred(true)
    } else {
      setHasGenerateTriggred(false)
    }
    setQnas(props.qas)
  }, [props.qas])

  useEffect(() => {
    setLastOffset(props.last_offset ? props.last_offset : lastOffset)
  }, [props.last_offset])

  useEffect(() => {}, [props.retryExecution])

  const removeKeywordFromList = (index: number) => {
    const oldAnswers = [...props.qas]
    const splicedData: any = oldAnswers[index]
    oldAnswers.splice(index, 1)
    props.updatedAnswersHandler({
      documentId: props._id,
      qas: oldAnswers,
    })

    // Only delete if id is present otherwise let it be
    if (splicedData._id) {
      dispatch(deleteQuestionFromDocument(props._id, splicedData._id))
    }
  }

  const dataChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const index = +e.target.dataset.index
    const toUpdateValue = e.target.dataset.value
    const oldAnswers: any[] = [...props.qas]
    const oldQuestionValue = { ...oldAnswers[index] }
    oldAnswers[index][toUpdateValue] = value
    props.updatedAnswersHandler({
      documentId: props._id,
      qas: oldAnswers,
    })
    if (oldQuestionValue.question !== e.target.value) {
    }
  }

  // const onEditorStateChange = (index: number, name: any) => {
  //   const value = name
  //   const toUpdateValue = 'answer'
  //   const oldAnswers: any[] = [...props.qas]
  //   const oldAnswerValue = oldAnswers[index]
  //   const convertedData = convertDataToMarkdownFormat(oldAnswerValue.answer)
  //   const convertedName = convertDataToMarkdownFormat(name)
  //   oldAnswers[index][toUpdateValue] = value
  //   props.updatedAnswersHandler({
  //     documentId: props._id,
  //     qas: oldAnswers,
  //   })
  //   if (convertedName !== convertedData) {
  //     setQuery({
  //       ...query,
  //       ...oldAnswers[index],
  //     })
  //   }
  // }

  const handleAnswerChange = (index: number, htmlValue: string) => {
    const toUpdateValue = 'answer'
    const oldAnswers: any[] = [...props.qas]
    const oldAnswerValue = oldAnswers[index]
    const convertedData = convertDataToMarkdownFormat(oldAnswerValue.answer)
    const convertedName = convertDataToMarkdownFormat(htmlValue)
    oldAnswers[index][toUpdateValue] = htmlValue
    props.updatedAnswersHandler({
      documentId: props._id,
      qas: oldAnswers,
    })
    if (convertedName !== convertedData) {
      setQuery({
        ...query,
        ...oldAnswers[index],
      })
    }
  }

  const startEditQuestionHandler = (index: number) => {
    const oldAnswers: any[] = [...props.qas]
    const oldAnswerValue = oldAnswers[index]
    oldAnswers[index]['hasEditMode'] = oldAnswerValue.hasOwnProperty(
      'hasEditMode',
    )
      ? !oldAnswerValue.hasEditMode
      : true
    props.updatedAnswersHandler({
      documentId: props._id,
      qas: oldAnswers,
    })
    setQnas([...oldAnswers])
  }

  const saveQuestionHandler = (question: any, index: number) => {
    try {
      dispatch(
        updateQuestionFromDocument(props._id, question._id, {
          question: question.question,
          answer: convertDataToMarkdownFormat(question.answer),
        }),
      )
      startEditQuestionHandler(index)
    } catch (error) {
      console.log('[ error ]', error)
    }
  }

  const previewToggleHandler = async (index?: number) => {
    if (index >= 0) {
      const oldAnswers: any[] = [...props.qas]
      props.setAnswerData({
        organisation_id: props.organisation_id,
        question: oldAnswers[index].question,
        _id: props._id,
        keyword: convertDataToMarkdownFormat(oldAnswers[index].answer),
        text_block_id: oldAnswers[index].text_block_id,
      })
    }
  }

  const generateCustomQuestionAndAnswerHandler = async (index: number) => {
    try {
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(true)
      }
      const oldAnswers: any[] = [...Qnas]
      const questionAnswer = oldAnswers[index]
      const response: any = await dispatch(
        generateCustomQuestionAnswer(props._id, questionAnswer.question),
      )
      delete questionAnswer.newlyAdded
      questionAnswer._id = response._id
      questionAnswer.answer = convertDataToMarkdownFormat(
        response.answer.trim().length
          ? response.answer
          : 'We are unable to fetch answer based on your question. Please insert manually.',
      )
      props.updatedAnswersHandler({
        documentId: props._id,
        qas: oldAnswers,
      })
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(false)
      }
    } catch (error) {
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(false)
      }
    }
  }

  const applyDrag = (arr: any, dragResult: any) => {
    const updatedArray: any[] = [...arr]
    const { removedIndex, addedIndex, payload } = dragResult
    const addedSortOrder = arr[addedIndex].sort_order
    const removedSortOrder = arr[removedIndex].sort_order
    updatedArray[removedIndex].sort_order = addedSortOrder
    updatedArray[addedIndex].sort_order = removedSortOrder

    if (removedIndex === null && addedIndex === null) return updatedArray

    const result = [...updatedArray]
    let itemToAdd = payload
    if (removedIndex !== null) {
      itemToAdd = result.splice(removedIndex, 1)[0]
    }

    if (addedIndex !== null) {
      result.splice(addedIndex, 0, itemToAdd)
    }

    if (result && result.length > 0) {
      const toUpdateQna: any[] = []
      const sort_order = result[0].sort_order
      result.map((res: any, index: number) => {
        toUpdateQna.push({
          _id: res._id,
          sort_order: sort_order + (index + 1),
        })
        return {
          ...res,
          sort_order: sort_order + (index + 1),
        }
      })

      dispatch(updateQnasSortOrder(props._id, toUpdateQna))
    }

    props.updatedAnswersHandler({
      documentId: props._id,
      qas: [...result],
    })
    setQnas([...result])
    return result
  }

  const generateQuestionsHandler = async (
    formValue: string = 'generate',
    max_que: number = 1,
    byPassedGenerateQnaForm = {},
  ) => {
    setHasGenerateTriggred(true)
    try {
      let byPassedGenerateQnaFormAsObject: any = { ...generateQnaForm }
      if (Object.keys(byPassedGenerateQnaForm).length) {
        byPassedGenerateQnaFormAsObject = { ...byPassedGenerateQnaForm }
      }

      setGenerateQnaForm({
        ...byPassedGenerateQnaFormAsObject,
        generateLoading: true,
      })
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(true)
      }
      let maxQue = max_que
      const response: any = await dispatch(
        generateQuestionAnswerBasedonNumberOfInput(
          props._id,
          +generateQnaForm[formValue] <= 0
            ? max_que
            : +generateQnaForm[formValue],
          source,
          questionType?.value,
        ),
      )

      if (!response.length && byPassedGenerateQnaFormAsObject.apiCount === 3) {
        setGenerateQnaForm({
          ...byPassedGenerateQnaFormAsObject,
          error: true,
          apiCount: 0,
        })

        if (setInterval) {
          clearTimeout(setInterval)
        }
        return
      }

      if (!response.length) {
        setInterval = setTimeout(() => {
          const obj: any = {
            ...byPassedGenerateQnaFormAsObject,
            apiCount: byPassedGenerateQnaFormAsObject.apiCount + 1,
          }
          setGenerateQnaForm(obj)
          generateQuestionsHandler('generate', maxQue, obj)
        }, 10 * 1000)
      } else {
        props.updatedAnswersHandler({
          documentId: props._id,
          qas: [...response],
        })
        if (props.setAnswerGenerationProcessing) {
          props.setAnswerGenerationProcessing(false)
        }
        setGenerateQnaForm({
          ...byPassedGenerateQnaFormAsObject,
          generateLoading: false,
          apiCount: 0,
          error: false,
        })
      }
    } catch (error: any) {
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(false)
      }
      setGenerateQnaForm({
        ...generateQnaForm,
        error: true,
      })
      console.log(error)
    }
  }

  const reGenerateQuestionsHandler = async () => {
    try {
      setGenerateQnaForm({
        ...generateQnaForm,
        retryLoading: true,
      })
      setHasGenerateTriggred(false)

      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(true)
      }
      const response: any = await dispatch(
        generateQuestionAnswerBasedonNumberOfInput(
          props._id,
          generateQnaForm['retry'] ? +generateQnaForm['retry'] : 2,
          source,
          questionType.value,
        ),
      )
      const fetchedQnas = orderBy(
        Qnas.concat(response),
        ['sort_order'],
        ['asc'],
      )
      setQnas([...fetchedQnas])
      props.updatedAnswersHandler({
        documentId: props._id,
        qas: fetchedQnas,
      })
      setHasGenerateTriggred(true)
      setGenerateQnaForm({
        ...generateQnaForm,
        retryLoading: false,
      })
    } catch (error: any) {
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(false)
      }
      setGenerateQnaForm({
        ...generateQnaForm,
        retryLoading: false,
      })
      setHasGenerateTriggred(true)
    }
  }

  const refreshQuestionsHandler = async () => {
    try {
      setGenerateQnaForm({
        ...generateQnaForm,
        summaryLoading: true,
      })
      setHasGenerateTriggred(false)

      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(true)
      }
      const { qas }: any = await dispatch(
        loadMoreQuestionsAndAnswersBasedOnDocument(
          props,
          0,
          Number.MAX_SAFE_INTEGER,
          null,
        ),
      )
      setQnas([...qas])
      props.updatedAnswersHandler({
        documentId: props._id,
        qas: qas,
      })
      setHasGenerateTriggred(true)
      setGenerateQnaForm({
        ...generateQnaForm,
        summaryLoading: false,
      })
    } catch (error: any) {
      if (props.setAnswerGenerationProcessing) {
        props.setAnswerGenerationProcessing(false)
      }
      setHasGenerateTriggred(true)
      setGenerateQnaForm({
        ...generateQnaForm,
        summaryLoading: false,
      })
    }
  }

  return (
    <React.Fragment>
      {props.isSummarisedWithTypeSummary &&
        props.isSummarised &&
        Qnas &&
        Qnas.length > 0 && (
          <>
            {!generateQnaForm.summaryLoading && (
              <div>
                {t("Click 'Refresh' to check if there are any unloaded Q&A")}{' '}
                <span>
                  {' '}
                  <Button
                    variant="text"
                    className="third-step mr-3"
                    onClick={() => {
                      refreshQuestionsHandler()
                    }}
                  >
                    {t('Refresh')}
                  </Button>
                </span>
              </div>
            )}

            {generateQnaForm.summaryLoading && (
              <div className="doc-processing d-flex flex-wrap align-items-center p-0 justify-content-center mb-3">
                <div className="col-status">
                  <span className="doc-loader spinner-width-20"></span>
                </div>
                <div className="pl-3 font-1">
                  {t('Generating tutoring questions')}....
                </div>
              </div>
            )}
          </>
        )}

      {!generateQnaForm.generateLoading &&
        !generateQnaForm.error &&
        (!Qnas || Qnas.length === 0) && (
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <h2 className="clr-primary">
                  {t('How many questions would you like to generate?')}
                </h2>
                <div className="font-16 d-inline-block mr-2">
                  {t('I want to generate a maximum of')}
                  <input
                    type="text"
                    className="clr-primary text-center radius-2 mx-2 px-1 border-grey py-2"
                    style={{ width: '2.5rem' }}
                    defaultValue={generateQnaForm.generate}
                    onChange={event => {
                      if (event.target.value.match(/^[0-9]*(\.[0-9]{0,2})?$/)) {
                        if (+event.target.value > 50) {
                          helper.showToaster(
                            t(
                              'Maximum number of question that can be generated is 50.',
                            ),
                          )
                          return
                        }

                        setGenerateQnaForm({
                          ...generateQnaForm,
                          generate: event.target.value,
                        })
                      }
                    }}
                  />
                  {props.type === 'summary' && (
                    <div className="d-inline-block" style={{ width: '110px' }}>
                      <Select
                        menuPosition="fixed"
                        classNamePrefix="select-target-qna"
                        className="select-target-qnas select-blue"
                        isSearchable={false}
                        components={{
                          DropdownIndicator: () => (
                            <KeyboardArrowDownIcon
                              fontSize="small"
                              color="primary"
                            />
                          ),
                          IndicatorSeparator: () => null,
                          ClearIndicator: () => null,
                        }}
                        options={questionTypes}
                        onChange={(e: any) => {
                          setQuestionType(e)
                        }}
                        value={questionType}
                      />
                    </div>
                  )}
                  {props.type === 'summary'
                    ? t(' questions')
                    : t('tutoring questions')}
                </div>
                <div className="font-16 d-inline-block mr-2 pb-4">
                  {t(
                    'Note: Number of questions generated may vary based on document length.',
                  )}
                </div>
                <Button
                  variant="contained"
                  onClick={() => {
                    generateQuestionsHandler('generate')
                  }}
                >
                  {t('Generate')}
                </Button>
              </div>
              <div className="d-flex align-items-center doc-count"> </div>
            </div>
          </div>
        )}

      <div className="generated-questions generated-question-answers drag-questions-wrap select-quiz-ques">
        {Qnas && Qnas.length > 0 && (
          //   <button
          //     className="btn btn-link mb-32 p-0"
          //     onClick={loadMoreQnasHandler}
          //   >
          //     {t("Load More Questions")}
          //   </button>
          <>
            {generateQnaForm.retryLoading && props.isSummarised && (
              <div className="doc-processing d-flex flex-wrap align-items-center p-0 mb-3">
                <div className="col-status">
                  <span className="doc-loader spinner-width-20"></span>
                </div>
                <div className="pl-1 font-1 font-600">
                  {t('Generating tutoring questions')}....
                </div>
              </div>
            )}

            {!generateQnaForm.retryLoading && (
              <div className="mb-3 d-flex flex-wrap justify-content-between">
                <div className="font-16 font-600 d-inline-block mr-2">
                  {t('Generate another')}
                  <input
                    type="text"
                    className="clr-primary text-center radius-2 mx-2 px-1 border-grey py-2"
                    style={{ width: '2.5rem' }}
                    defaultValue={
                      generateQnaForm.retry ? generateQnaForm.retry : 2
                    }
                    onChange={event => {
                      if (event.target.value.match(/^[0-9]*(\.[0-9]{0,2})?$/)) {
                        if (+event.target.value > 50) {
                          helper.showToaster(
                            t(
                              'Maximum number of question that can be generated is 50.',
                            ),
                          )
                          return
                        }

                        setGenerateQnaForm({
                          ...generateQnaForm,
                          retry: event.target.value,
                        })
                      }
                    }}
                  />
                  {props.type === 'summary' && (
                    <div className="d-inline-block" style={{ width: '110px' }}>
                      <Select
                        menuPosition="fixed"
                        classNamePrefix="select-target-qna"
                        className="select-target-qnas select-blue"
                        isSearchable={false}
                        components={{
                          DropdownIndicator: () => (
                            <KeyboardArrowDownIcon
                              fontSize="small"
                              color="primary"
                            />
                          ),
                          IndicatorSeparator: () => null,
                          ClearIndicator: () => null,
                        }}
                        options={questionTypes}
                        onChange={(e: any) => {
                          setQuestionType(e)
                        }}
                        value={questionType}
                      />
                    </div>
                  )}

                  {props.type === 'summary'
                    ? t(' questions')
                    : t('  tutoring questions')}
                </div>
                <Button variant="text" onClick={reGenerateQuestionsHandler}>
                  {t('Generate Questions')}
                </Button>
              </div>
            )}
          </>
        )}
        {generateQnaForm.error && (
          <div className="doc-processing d-flex flex-wrap align-items-center p-0 mb-3">
            <ErrorOutlineIcon color="error" fontSize="small" />
            <div className="col-msg pl-3 font-1">
              <div className="mb-1">
                {!props.toolkitGenerateQna && (
                  <span>
                    <span
                      className="font-600"
                      style={{
                        color: '#D00026',
                      }}
                    >
                      {t('Opps, we have encountered an error.')}
                    </span>
                    <br />
                  </span>
                )}

                {t(
                  'Walter was unable to generate questions and answers. Would you like to try again?',
                )}
              </div>
              <Button
                variant="contained"
                onClick={() => {
                  setGenerateQnaForm({
                    ...generateQnaForm,
                    apiCount: 0,
                    error: false,
                  })
                  generateQuestionsHandler('generate')
                }}
              >
                {t('Try Again')}
              </Button>
            </div>
          </div>
        )}

        {(!Qnas || Qnas.length === 0) &&
          props.isSummarised &&
          generateQnaForm.generateLoading &&
          !generateQnaForm.error && (
            <div className="doc-processing d-flex flex-wrap align-items-center p-0">
              <div className="col-status">
                <span className="doc-loader spinner-width-20"></span>
              </div>
              <div className="col-msg pl-3 font-1">
                {t(
                  'Generating Q&A from document....This process might take up to 10 mins While waiting, you may explore other parts of the platform. You will receive a notification when the Q&As have been generated.',
                )}
              </div>
            </div>
          )}

        <Container
          dragHandleSelector=".column-drag-handle"
          onDrop={e => (props.isQuizView ? () => {} : applyDrag(Qnas, e))}
          nonDragAreaSelector={'.que-ans-edit'}
        >
          {Qnas &&
            Qnas.map((question: any, index: number) => {
              return (
                <Draggable
                  key={props._id + 'draggable_' + index}
                  className={classNames({
                    'p-0': props.isQuizView,
                    'pr-0': true,
                  })}
                >
                  <div
                    className={classNames({
                      'draggable-item web-links column-drag-handle': true,
                      'edit-drag': !question.hasEditMode,
                    })}
                    style={{ position: 'relative' }}
                  >
                    {!props.isQuizView && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          zIndex: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{ padding: '8px' }}
                          aria-label={t('Drag to reorder')}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </IconButton>
                      </div>
                    )}

                    <ul
                      className="list-unstyled ques-list"
                      key={props._id + '_ANS_' + index}
                      id={props._id + '_ANS_' + index}
                      style={{ paddingLeft: 0 }}
                    >
                      <li className={question.hasEditMode && 'que-ans-edit'}>
                        <span className="font-weight-bold mr-2">Q:</span>

                        <AutoTextArea
                          key={props._id + '_QUE_' + index}
                          type="text"
                          className="form-control"
                          data-value={'question'}
                          data-index={index}
                          value={question?.question || ''}
                          onChangeHandler={dataChangeHandler}
                          readOnly={!question.hasEditMode}
                        />
                      </li>
                      <li className={question.hasEditMode && 'que-ans-edit'}>
                        {question.hasOwnProperty('newlyAdded') &&
                          question.newlyAdded &&
                          question.answer.trim().length === 0 &&
                          question.question.trim().length > 0 && (
                            <>
                              <span className="font-weight-bold mr-2 ans-varient">
                                A:
                              </span>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                  generateCustomQuestionAndAnswerHandler(index)
                                }}
                              >
                                {t('Generate Answer')}
                              </Button>
                            </>
                          )}

                        {!question.hasOwnProperty('newlyAdded') &&
                          question.answer && (
                            <>
                              <span className="font-weight-bold mr-2 ans-varient">
                                {t('A:')}
                              </span>
                              {question.hasEditMode ? (
                                <BrainlyMathEditorSimple
                                  key={
                                    question._id ||
                                    `${props._id}_answer_${index}`
                                  }
                                  value={question?.answer || ''}
                                  onChange={value =>
                                    handleAnswerChange(index, value)
                                  }
                                  placeholder={t('Type answer here')}
                                  height="200px"
                                  minHeight="150px"
                                  readOnly={false}
                                />
                              ) : (
                                <RichMarkdownContent
                                  contextText={question?.answer || ''}
                                />
                              )}
                            </>
                          )}
                      </li>
                      {!props.isQuizView && (
                        <div className="edit-action-btns">
                          {!question.hasEditMode ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  startEditQuestionHandler(index)
                                }}
                                aria-label={t('Edit question')}
                              >
                                <EditIcon fontSize="small" color="primary" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  removeKeywordFromList(index)
                                }}
                                aria-label={t('Delete question')}
                              >
                                <CloseIcon fontSize="small" color="error" />
                              </IconButton>
                            </>
                          ) : (
                            !question.hasOwnProperty('newlyAdded') &&
                            question.answer && (
                              <IconButton
                                size="small"
                                onClick={() => previewToggleHandler(index)}
                                aria-label={t('Preview answer')}
                              >
                                <SearchIcon fontSize="small" color="primary" />
                              </IconButton>
                            )
                          )}
                        </div>
                      )}
                      {question.hasEditMode && (
                        <div className="text-right edit-done-btns pb-2">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              saveQuestionHandler(question, index)
                            }}
                            disabled={question.newlyAdded && !question.answer}
                          >
                            {t('Save')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            className="ml-2"
                            onClick={() => {
                              startEditQuestionHandler(index)
                            }}
                          >
                            {t('Cancel')}
                          </Button>
                        </div>
                      )}
                      <div ref={props?.lastQuesRef} />
                    </ul>
                  </div>
                </Draggable>
              )
            })}
        </Container>

        {(generateQnaForm.error || hasGenerateTriggred) &&
          props.addMoreQuestionHandler && (
            <div className="text-center create-from-scratch">
              <div className="my-3 font-16">
                {/* {t("Can't find your question and answer ?")} */}
                <Button
                  variant="outlined"
                  onClick={e => {
                    props.addMoreQuestionHandler(e, props._id)
                  }}
                >
                  <span className="mr-3">+</span>
                  {t('Create from Scratch')}
                </Button>
              </div>
            </div>
          )}
      </div>
    </React.Fragment>
  )
}

export default QAnsAListSummarised
